import React, { useEffect, useMemo, useState } from "react";
import Client from "../../models/client";
import { clearMatchedCvFilter, IncentiveListAction, openIncentiveListModal } from "../actions/incentiveList";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { CvListData, CVListTable, CvRow } from "./CVListTable";
import { FETCH_KEYS, fetcherWithAuth } from "../api";
import { CVListSearchForm, FormData } from "./CVListSearchForm";
import { confirmIncentiveList } from "../api/incentiveListApi";
import { notifications } from "@mantine/notifications";
import { Alert, alpha, Box, Button, Group, Text, ThemeIcon, Title } from "@mantine/core";
import { FlagIcon } from "@phosphor-icons/react";
import { IncentiveListGenerateButton } from "./cv/incentive/IncentiveListGenerateButton";
import { IncentiveConfirmButton } from "./cv/incentive/IncentiveConfirmButton";
import { IncentiveListModal } from "../containers/cv/IncentiveListModal";
import { State } from "../reducers";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { CV_LIST_INITIAL_PRESET, resolveDateParams } from "../utils/datePreset";
import { confirmIssueIncentive } from "../utils/confirm";

interface OwnProps {
  client: Client
}

interface StateProps {
  matchedTokeys: string[] | null
  cvRows: Record<string, any>[] | null
}

interface DispatchProps {
  openIncentiveListModal: typeof openIncentiveListModal
  clearMatchedCvFilter: typeof clearMatchedCvFilter
}

type Props = OwnProps & StateProps & DispatchProps

const matchBannerStyles = {
  root: { background: alpha('var(--mantine-color-invy-6)', 0.06), border: '1px solid var(--mantine-color-invy-6)' },
}

const CVList: React.FC<Props> = ({
  client,
  matchedTokeys,
  cvRows,
  openIncentiveListModal,
  clearMatchedCvFilter,
}) => {
  const navigate = useNavigate()

  // 初回フェッチも検索フォームの初期プリセット（今月）に合わせる
  const [params, setParams] = useState(() => ({
    duplicate: false,
    is_test: false,
    ...resolveDateParams(CV_LIST_INITIAL_PRESET, [null, null]),
  }))
  const { data, error, mutate, isLoading } = useSWR<CvListData>(
    FETCH_KEYS.fetchCvList(client.id, params.date, params.duplicate, params.is_test, params.startDate, params.endDate),
    fetcherWithAuth,
    { revalidateOnFocus: false },
  )

  const [selectedTokeys, setSelectedTokeys] = useState<string[]>([])

  const [confirming, setConfirming] = useState(false)

  const isFiltering = matchedTokeys !== null
  const matchedCount = matchedTokeys?.length ?? 0

  const filterSignature = matchedTokeys ? matchedTokeys.join(',') : 'none'

  useEffect(() => {
    setSelectedTokeys([])
  }, [filterSignature])

  const handleSearchForm = (formValues: FormData) => {
    setParams({
      ...formValues,
      startDate: formValues.startDate ?? '',
      endDate: formValues.endDate ?? '',
    })
  }

  const tableData = useMemo<CvListData>(() => {
    const baseColumns = data?.columns ?? []
    const baseRows = isFiltering ? (cvRows ?? []) : (data?.rows ?? [])

    const rowWithId = baseRows.map((row, i) => ({
      ...row,
      __rowId__: `${(row as any)?.tokey ?? 'row'}#${i}`,
    })) as CvRow[]
    return { columns: baseColumns, rows: rowWithId }
  }, [data, isFiltering, cvRows])

  const selectedRecords = useMemo<CvRow[]>(() => {
    if (selectedTokeys.length === 0) return []
    return tableData.rows.filter((r) =>
      selectedTokeys.includes(String((r as any)?.tokey ?? '')),
    )
  }, [selectedTokeys, tableData.rows])

  const isRecordSelectable = (r: CvRow) =>
    !Number((r as any)?.gave_incentive) && (r as any)?.rid !== 'test'

  const idAccessor = (r: CvRow) => String((r as any)?.__rowId__ ?? '')

  const tokeyOf = (r: CvRow) => {
    const t = (r as any)?.tokey
    return typeof t === 'string' && t.length > 0 ? t : null
  }

  // DataTable は現ページ分だけでなく選択中の全レコードを返すため、他ページの選択はそのまま残る。
  // 選択はtokey（対象者）単位で、同一tokeyが達成日違いで複数行あっても対象者ごとまとめて増減する。
  const handleSelectionChange = (records: CvRow[]) => {
    // 同一tokeyが複数行あると、1行外しても兄弟行から同じtokeyが拾われてしまう。
    // 選択が外れた行のtokeyは明示的に除外し、対象者ごとまとめて解除する。
    const nextRowIds = new Set(records.map(idAccessor))
    const removedTokeys = new Set(
      selectedRecords
        .filter((r) => !nextRowIds.has(idAccessor(r)))
        .map(tokeyOf)
        .filter((t): t is string => t !== null),
    )
    const tokeys = records.map(tokeyOf).filter((t): t is string => t !== null)
    setSelectedTokeys(
      Array.from(new Set(tokeys)).filter((t) => !removedTokeys.has(t)),
    )
  }

  const handleConfirmSelection = async () => {
    if (selectedTokeys.length === 0) return
    if (!confirmIssueIncentive(selectedTokeys.length)) return
    setConfirming(true)
    try {
      await confirmIncentiveList(client.id, selectedTokeys)
      await mutate()
      setSelectedTokeys([])
    } catch (e) {
      console.error('特典の発行確定に失敗しました', e)
      notifications.show({ message: '発行確定に失敗しました', color: 'red' })
    } finally {
      setConfirming(false)
    }
  }

  const handleGenerated = () => {
    navigate(`/admin/clients/${client.url_name}/incentive-lists-saved`)
  }

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant="transparent">
          <FlagIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">CV一覧</Title>
      </Group>

      <Text fz={14} c="#000000CC">
        「CV一覧」では、発生したCVの一覧を確認できます。<br />
        特典付与の対象確認や、CV検証時に各データが正しく取得されているかの確認にご利用ください。
      </Text>

      <CVListSearchForm onSubmit={handleSearchForm} />
      {error && <Text c="red">読み込みに失敗しました</Text>}

      <Group justify="space-between" align="center" mt={16} mb={8}>
        <Text fz={13} c="#666">
          特典付与リスト生成時は対象ユーザーにチェックを入れるか、「CSVからリスト生成」から作成してください。
        </Text>
        <Button
          variant="outline"
          radius="xl"
          onClick={() => openIncentiveListModal()}
        >
          CSVからリスト生成
        </Button>
      </Group>

      {!isFiltering && selectedTokeys.length > 0 && (
        <Alert mb={12} styles={matchBannerStyles}>
          <Group justify="space-between" wrap="nowrap">
            <Text>
              特典付与リスト生成で絞り込み中（CV {selectedRecords.length}件 / 対象者 {selectedTokeys.length}名）
            </Text>
            <Group gap="xs">
              <IncentiveListGenerateButton
                clientId={client.id}
                selectedTokeys={selectedTokeys}
                onGenerated={handleGenerated}
              />
              <Button
                variant="filled"
                radius="xl"
                size="sm"
                onClick={handleConfirmSelection}
                disabled={confirming}
                loading={confirming}
              >
                発行確定
              </Button>
            </Group>
          </Group>
        </Alert>
      )}

      {isFiltering && matchedCount > 0 && (
        <Alert mb={12} styles={matchBannerStyles}>
          <Group justify="space-between" wrap="nowrap">
            <Text>
              特典付与リスト生成で絞り込み中（CV {tableData.rows.length}件 / 対象者 {matchedCount}名）
              {selectedTokeys.length > 0 && (
                <Text component="span" ml={8}>
                  （対象者 {selectedTokeys.length}名選択中）
                </Text>
              )}
            </Text>
            <Group gap="xs">
              <IncentiveListGenerateButton
                clientId={client.id}
                selectedTokeys={selectedTokeys}
                onGenerated={handleGenerated}
              />
              <IncentiveConfirmButton
                clientId={client.id}
                selectedTokeys={selectedTokeys}
              />
              <Button
                variant="outline"
                radius="xl"
                color="gray"
                size="sm"
                onClick={() => clearMatchedCvFilter()}
              >
                絞り込みを解除
              </Button>
            </Group>
          </Group>
        </Alert>
      )}

      {isFiltering && matchedCount === 0 && (
        <Alert color="yellow" variant="light" mb={12}>
          <Group justify="space-between" wrap="nowrap">
            <Text>該当するCVがありませんでした（0件）。CSVの列・値をご確認ください。</Text>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              size="sm"
              onClick={() => clearMatchedCvFilter()}
            >
              絞り込みを解除
            </Button>
          </Group>
        </Alert>
      )}
      <CVListTable
        data={tableData}
        storageKey={`cvList:visibleFields:${client.id}`}
        fetching={isLoading}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={handleSelectionChange}
        isRecordSelectable={isRecordSelectable}
        idAccessor={idAccessor}
        onFiltersChange={() => setSelectedTokeys([])}
      />

      <IncentiveListModal clientId={client.id} />
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => ({
  matchedTokeys: state.incentiveList.matchedTokeys ?? null,
  cvRows: state.incentiveList.cvRows ?? null,
})

const mapDispatchToProps = (dispatch: Dispatch<IncentiveListAction>): DispatchProps =>
  bindActionCreators({ openIncentiveListModal, clearMatchedCvFilter }, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(
  CVList as any,
) as any as React.ComponentType<OwnProps>
